import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ACWR_ZONES,
  MONOTONY_WARN_THRESHOLD,
  DELTA_WARN_PCT as CALC_DELTA_WARN_PCT,
  DELTA_ALERT_PCT as CALC_DELTA_ALERT_PCT,
} from '@leopart/calc';
import {
  effortAthleteEndpoints,
  IEffortAthleteResponse,
  IEffortSeriesPoint,
} from '../../endpoints/effort-athlete';

// ── Soglie (spec 2026-07-26-tab-sforzo-design.md) ─────────────────────────────

// Soglie centralizzate in @leopart/calc (fonte unica con il backend).
export const ACWR_MIN = ACWR_ZONES.okMin;
export const ACWR_MAX = ACWR_ZONES.warnMax;
/** |Δ%| oltre cui il delta diventa ambra */
export const DELTA_WARN_PCT = CALC_DELTA_WARN_PCT;
/** |Δ%| oltre cui il delta diventa rosso */
export const DELTA_DANGER_PCT = CALC_DELTA_ALERT_PCT;
/** Monotonia oltre cui il valore diventa ambra */
export const MONOTONY_WARN = MONOTONY_WARN_THRESHOLD;

export type KpiTone = 'neutral' | 'warning' | 'danger';

// ── Helper puri (esportati per riuso/test) ────────────────────────────────────

export function isAcwrOutOfRange(acwr: number | null | undefined): boolean {
  return acwr != null && (acwr < ACWR_MIN || acwr > ACWR_MAX);
}

export function getDeltaTone(deltaPct: number | null | undefined): KpiTone {
  if (deltaPct == null) return 'neutral';
  const abs = Math.abs(deltaPct);
  if (abs > DELTA_DANGER_PCT) return 'danger';
  if (abs > DELTA_WARN_PCT) return 'warning';
  return 'neutral';
}

export function getMonotonyTone(monotony: number | null | undefined): KpiTone {
  return monotony != null && monotony > MONOTONY_WARN ? 'warning' : 'neutral';
}

export function formatAu(value: number | null | undefined): string {
  if (value == null) return '—';
  return Math.round(value).toLocaleString('it-IT');
}

/** Δ% con segno esplicito; null (sett. precedente = 0) → null (nascosto) */
export function formatDeltaPct(deltaPct: number | null | undefined): string | null {
  if (deltaPct == null) return null;
  const rounded = Math.round(deltaPct);
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

interface UseAthleteEffortParams {
  athleteId: string;
  /** Opzionale: senza teamId il BE risolve l'atleta senza vincolo di roster */
  teamId?: string;
  /** Finestra in giorni (default 28) */
  days?: number;
}

export function useAthleteEffort({ athleteId, teamId, days = 28 }: UseAthleteEffortParams) {
  const query = useQuery<IEffortAthleteResponse>({
    queryKey: ['effort-athlete', athleteId, teamId ?? null, days],
    queryFn: async () => {
      const { data } = await effortAthleteEndpoints.getAthleteEffort(athleteId, { teamId, days });
      return data;
    },
    enabled: !!athleteId,
  });

  const kpi = query.data?.kpi ?? null;
  const series: IEffortSeriesPoint[] = useMemo(
    () => query.data?.series ?? [],
    [query.data],
  );

  // Badge header: fuori fascia 0.80–1.30 (flag BE con fallback calcolato)
  const acwrOutOfRange = kpi ? (kpi.outOfRange ?? isAcwrOutOfRange(kpi.acwr)) : false;

  const athleteFullName = useMemo(() => {
    const a = query.data?.athlete;
    if (!a) return null;
    const name = [a.firstName, a.lastName].filter(Boolean).join(' ').trim();
    return name.length > 0 ? name : null;
  }, [query.data]);

  // ── KPI formattati + tone per le card ────────────────────────────────────

  const weekLoadLabel = formatAu(kpi?.weekLoad);
  const deltaLabel = formatDeltaPct(kpi?.deltaPct);
  const deltaTone = getDeltaTone(kpi?.deltaPct);
  const monotonyLabel = kpi?.monotony != null ? kpi.monotony.toFixed(2) : '—';
  const monotonyTone = getMonotonyTone(kpi?.monotony);
  const strainLabel = formatAu(kpi?.strain);
  const acwrLabel = kpi?.acwr != null ? kpi.acwr.toFixed(2) : null;

  return {
    // Query state
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,

    // Dati grezzi
    kpi,
    series,
    athleteFullName,

    // Derivati per il rendering
    acwrOutOfRange,
    acwrLabel,
    weekLoadLabel,
    deltaLabel,
    deltaTone,
    monotonyLabel,
    monotonyTone,
    strainLabel,
  };
}
